import React, { useEffect, useState } from 'react';
import { Settings, HelpCircle, Heart, Calendar, CreditCard, LogOut, Edit2, Check, Plus, Trash2 } from 'lucide-react';
import { UserProfile, SpecialDate } from '../../types';
import { MOCK_DATES } from '../../data/mockData';
import { AccordionSection } from '../AccordionSection';
import { QRCodeDisplay } from '../QRCodeDisplay';
import { SpecialDateItem } from '../SpecialDateItem';
import { Button } from '../ui/Button';
import { useToast } from '../ToastContext';
import { triggerConfetti } from '../ui/ConfettiButton';
interface ProfileScreenProps {
  user: UserProfile;
}
export function ProfileScreen({
  user
}: ProfileScreenProps) {
  const [dates, setDates] = useState<SpecialDate[]>(MOCK_DATES);
  const [birthday, setBirthday] = useState(user.birthday || '');
  const [isEditingBirthday, setIsEditingBirthday] = useState(false);
  const [birthdayInput, setBirthdayInput] = useState(birthday);
  const [showAddDate, setShowAddDate] = useState(false);
  const [newDate, setNewDate] = useState({
    name: '',
    date: '',
    type: 'other' as const,
    notes: ''
  });
  const {
    showToast
  } = useToast();
  const handleSaveBirthday = () => {
    if (!birthdayInput.match(/^\d{2}-\d{2}$/)) {
      showToast('Please use MM-DD format', 'error');
      return;
    }
    setBirthday(birthdayInput);
    setIsEditingBirthday(false);
    localStorage.setItem('userBirthday', birthdayInput);
    triggerConfetti();
    showToast('Birthday saved! 🎉', 'success');
  };
  const handleDeleteDate = (id: string) => {
    setDates(prev => prev.filter(d => d.id !== id));
    showToast('Date removed', 'info');
  };
  const handleAddDate = () => {
    if (!newDate.name || !newDate.date) {
      showToast('Please fill in name and date', 'error');
      return;
    }
    if (!newDate.date.match(/^\d{2}-\d{2}$/)) {
      showToast('Please use MM-DD format', 'error');
      return;
    }
    const date: SpecialDate = {
      id: `d-${Date.now()}`,
      ...newDate
    };
    setDates(prev => [...prev, date]);
    setNewDate({
      name: '',
      date: '',
      type: 'other',
      notes: ''
    });
    setShowAddDate(false);
    showToast('Date added successfully', 'success');
  };
  const formatBirthday = (dateStr: string) => {
    if (!dateStr) return 'Not set';
    const [month, day] = dateStr.split('-');
    const date = new Date(2000, parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500" data-id="element-557">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8" data-id="element-558">
        <div className="w-20 h-20 rounded-full bg-gray-200 border-4 border-white shadow-md overflow-hidden" data-id="element-559">
          <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" data-id="element-560" />
        </div>
        <div data-id="element-561">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-562">{user.name}</h1>
          <p className="text-gray-500" data-id="element-563">{user.email}</p>
          <div className="flex items-center gap-2 mt-2" data-id="element-564">
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full" data-id="element-565">
              {user.points} Points
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-2" data-id="element-566">
        <AccordionSection title="Your User ID" icon={<div className="w-5 h-5" data-id="element-568" />} data-id="element-567">
          <div className="flex justify-center pb-4" data-id="element-569">
            <QRCodeDisplay value={user.id} avatar={user.avatar} data-id="element-570" />
          </div>
          <p className="text-xs text-center text-gray-500 mt-2" data-id="element-571">
            Let friends scan to send you gifts
          </p>
        </AccordionSection>

        <AccordionSection title="Special Dates" icon={<Calendar size={20} data-id="element-573" />} data-id="element-572">
          <div className="pb-4 space-y-4" data-id="element-574">
            {/* My Birthday Section */}
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-100" data-id="element-575">
              <div className="flex items-center justify-between mb-3" data-id="element-576">
                <div className="flex items-center gap-2" data-id="element-577">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center" data-id="element-578">
                    <Calendar size={16} className="text-purple-600" data-id="element-579" />
                  </div>
                  <span className="font-bold text-gray-900" data-id="element-580">My Birthday</span>
                </div>
                {!isEditingBirthday && <button onClick={() => {
                setIsEditingBirthday(true);
                setBirthdayInput(birthday);
              }} className="p-1.5 bg-white rounded-lg hover:bg-purple-100 transition-colors" data-id="element-581">
                    <Edit2 size={14} className="text-purple-600" data-id="element-582" />
                  </button>}
              </div>

              {isEditingBirthday ? <div className="flex items-center gap-2" data-id="element-583">
                  <input type="text" placeholder="MM-DD" value={birthdayInput} onChange={e => setBirthdayInput(e.target.value)} className="flex-1 px-3 py-2 rounded-lg border border-purple-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" maxLength={5} data-id="element-584" />
                  <button onClick={handleSaveBirthday} className="p-2 bg-[#FFFC00] rounded-lg hover:bg-[#E6E300] transition-colors" data-id="element-585">
                    <Check size={16} className="text-gray-900" strokeWidth={3} data-id="element-586" />
                  </button>
                  <button onClick={() => setIsEditingBirthday(false)} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-587">
                    <Trash2 size={16} className="text-gray-600" data-id="element-588" />
                  </button>
                </div> : <p className="text-gray-700 font-medium" data-id="element-589">
                  {formatBirthday(birthday)}
                </p>}
            </div>

            {/* Important Dates */}
            <div data-id="element-590">
              <div className="flex items-center justify-between mb-3" data-id="element-591">
                <p className="text-sm font-bold text-gray-500 uppercase" data-id="element-592">
                  Important Dates
                </p>
                <button onClick={() => setShowAddDate(!showAddDate)} className="p-1.5 bg-[#FFFC00] rounded-lg hover:bg-[#E6E300] transition-colors" data-id="element-593">
                  <Plus size={14} className="text-gray-900" strokeWidth={3} data-id="element-594" />
                </button>
              </div>

              {showAddDate && <div className="bg-gray-50 rounded-xl p-4 mb-3 space-y-3 border border-gray-200" data-id="element-595">
                  <input type="text" placeholder="Occasion name" value={newDate.name} onChange={e => setNewDate({
                ...newDate,
                name: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" data-id="element-596" />
                  <input type="text" placeholder="MM-DD" value={newDate.date} onChange={e => setNewDate({
                ...newDate,
                date: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" maxLength={5} data-id="element-597" />
                  <select value={newDate.type} onChange={e => setNewDate({
                ...newDate,
                type: e.target.value as any
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm" data-id="element-598">
                    <option value="birthday" data-id="element-599">Birthday</option>
                    <option value="anniversary" data-id="element-600">Anniversary</option>
                    <option value="holiday" data-id="element-601">Holiday</option>
                    <option value="other" data-id="element-602">Other</option>
                  </select>
                  <textarea placeholder="Notes (optional)" value={newDate.notes} onChange={e => setNewDate({
                ...newDate,
                notes: e.target.value
              })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500/20 text-sm h-20 resize-none" data-id="element-603" />
                  <div className="flex gap-2" data-id="element-604">
                    <Button size="sm" onClick={handleAddDate} className="flex-1" data-id="element-605">
                      Save
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setShowAddDate(false)} className="flex-1" data-id="element-606">
                      Cancel
                    </Button>
                  </div>
                </div>}

              {dates.length > 0 ? dates.map(date => <SpecialDateItem key={date.id} date={date} onDelete={handleDeleteDate} data-id="element-607" />) : <div className="text-center py-8 text-gray-400" data-id="element-608">
                  <Calendar size={32} className="mx-auto mb-2 opacity-50" data-id="element-609" />
                  <p className="text-sm" data-id="element-610">No dates added yet</p>
                  <button onClick={() => setShowAddDate(true)} className="mt-2 text-sm text-purple-600 font-medium" data-id="element-611">
                    Add Your First Date
                  </button>
                </div>}
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Your Rewards" icon={<div className="w-5 h-5 bg-purple-100 rounded-full" data-id="element-613" />} data-id="element-612">
          <div className="space-y-3 pb-2" data-id="element-614">
            <div className="p-3 bg-gray-50 rounded-xl" data-id="element-615">
              <div className="flex justify-between items-center mb-2" data-id="element-616">
                <span className="font-medium text-sm" data-id="element-617">Bean & Leaf</span>
                <span className="text-sm font-bold text-purple-600" data-id="element-618">
                  7/10 stamps
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-619">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: '70%'
              }} data-id="element-620" />
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-xl" data-id="element-621">
              <div className="flex justify-between items-center mb-2" data-id="element-622">
                <span className="font-medium text-sm" data-id="element-623">Urban Garden</span>
                <span className="text-sm font-bold text-purple-600" data-id="element-624">
                  3/10 stamps
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-625">
                <div className="bg-purple-600 h-2 rounded-full" style={{
                width: '30%'
              }} data-id="element-626" />
              </div>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection title="Saved Cards" icon={<CreditCard size={20} data-id="element-628" />} data-id="element-627">
          <div className="p-4 bg-gray-50 rounded-xl mb-2 flex items-center gap-3" data-id="element-629">
            <div className="w-10 h-6 bg-gray-800 rounded" data-id="element-630" />
            <span className="font-medium text-sm" data-id="element-631">•••• 4242</span>
            <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold" data-id="element-632">
              Default
            </span>
          </div>
        </AccordionSection>

        <AccordionSection title="Favorite Places" icon={<Heart size={20} data-id="element-634" />} data-id="element-633">
          <p className="text-sm text-gray-500 p-2" data-id="element-635">3 saved places</p>
        </AccordionSection>

        <AccordionSection title="Help & Support" icon={<HelpCircle size={20} data-id="element-637" />} data-id="element-636">
          <div className="space-y-2 pb-2" data-id="element-638">
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-639">
              FAQ
            </button>
            <button className="w-full text-left p-3 bg-gray-50 rounded-xl text-sm font-medium hover:bg-gray-100 transition-colors" data-id="element-640">
              Contact Support
            </button>
          </div>
        </AccordionSection>

        <div className="pt-6" data-id="element-641">
          <button className="flex items-center gap-3 text-red-500 font-medium px-1 hover:text-red-600 transition-colors" data-id="element-642">
            <LogOut size={20} data-id="element-643" /> Sign Out
          </button>
        </div>
      </div>
    </div>;
}